import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { Box } from '@mantine/core'
import { AppText } from '../../app/common/ui'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import { IoImageOutline } from 'react-icons/io5'
import { FirearmModel } from '../../app/models/firearmModel'
import { TrustBadge } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { VoteTargetType } from '../../app/api/agent'

interface Props {
  model: FirearmModel;
  onVote?: () => void;
}

function platformBadgeStyle (platform: string | null): Record<string, string> {
  if (!platform) return { background: 'var(--gs-surface2)', color: 'var(--gs-muted)', border: '1px solid var(--gs-border)' }
  const p = platform.toLowerCase()
  if (p.includes('ar-15') || p.includes('ak')) return { background: 'rgba(232,93,4,0.15)', color: '#E85D04', border: '1px solid rgba(232,93,4,0.4)' }
  if (p.includes('glock') || p.includes('1911') || p.includes('pistol')) return { background: 'rgba(56,139,253,0.15)', color: '#58A6FF', border: '1px solid rgba(56,139,253,0.4)' }
  if (p.includes('bolt') || p.includes('lever')) return { background: 'rgba(63,185,80,0.15)', color: '#3FB950', border: '1px solid rgba(63,185,80,0.4)' }
  if (p.includes('revolver') || p.includes('shotgun')) return { background: 'rgba(210,153,34,0.15)', color: '#D29922', border: '1px solid rgba(210,153,34,0.4)' }
  return { background: 'var(--gs-surface2)', color: 'var(--gs-muted)', border: '1px solid var(--gs-border)' }
}

const voteBtnBase = {
  background: 'var(--gs-surface2)',
  border: '1px solid var(--gs-border)',
  borderRadius: 4,
  padding: '4px 6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'border-color 0.15s, background 0.15s, color 0.15s',
  '&:hover': { borderColor: '#3FB950' },
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' }
}

export default observer(function RegistryModelCard ({ model, onVote }: Props) {
  const history = useHistory()
  const { userStore, voteStore, firearmModelStore } = useStore()
  const voteScore = model.voteScore ?? 0
  const buildCount = model.buildCount ?? 0
  const currentUserVote = model.currentUserVote ?? null
  const isOwn = userStore.user?.username === model.submittedByUsername

  const handleVote = async (e: React.MouseEvent, value: 1 | -1) => {
    e.stopPropagation()
    if (isOwn || voteStore.loading) return
    try {
      if (currentUserVote === value) {
        await voteStore.removeVote(VoteTargetType.FirearmModel, model.id)
      } else {
        await voteStore.castVote(VoteTargetType.FirearmModel, model.id, value)
      }
      onVote?.() ?? firearmModelStore.loadModels()
    } catch {
      // error handled by interceptor
    }
  }

  return (
    <Box
      component="article"
      onClick={() => history.push(`/registry/${model.id}`)}
      sx={{
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 150ms',
        '&:hover': { borderColor: '#E85D04' }
      }}
    >
      {/* Image area 100px */}
      <Box sx={{ position: 'relative', height: 100 }}>
        {model.coverImageUrl
          ? (
          <img
            src={model.coverImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
            )
          : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #21262D, #161B22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#30363D'
            }}
          >
            <IoImageOutline size={32} />
          </Box>
            )}
        {model.isVerified && (
          <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
            <TrustBadge variant="verified" size="xs" />
          </Box>
        )}
        {model.status === 0 && (
          <Box sx={{ position: 'absolute', bottom: 6, left: 6 }}>
            <TrustBadge variant="pending" size="xs" label="Pending" />
          </Box>
        )}
        {model.status === 2 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 6,
              left: 6,
              background: 'rgba(248,81,73,0.2)',
              border: '1px solid rgba(248,81,73,0.4)',
              color: '#f85149',
              padding: '2px 6px',
              borderRadius: 3,
              fontSize: 9,
              fontWeight: 700
            }}
          >
            Rejected
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ padding: 'var(--gs-xs) var(--gs-sm)' }}>
        <Box sx={{ marginBottom: 'var(--gs-xs)' }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              ...platformBadgeStyle(model.platform)
            }}
          >
            {model.platform || 'Other'}
          </Box>
        </Box>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--gs-text)',
            marginTop: 'var(--gs-xs)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {model.make} {model.model}
        </AppText>
        {model.caliber && (
          <AppText sx={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--gs-muted)', marginBottom: 'var(--gs-sm)', letterSpacing: 0.3 }}>
            {model.caliber}
          </AppText>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 'var(--gs-xs)'
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {isOwn
              ? (
              <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, fontWeight: 700, color: '#3FB950' }}>
                ▲ {voteScore}
              </AppText>
                )
              : (
              <>
                <Box
                  component="button"
                  type="button"
                  disabled={voteStore.loading}
                  onClick={(e) => handleVote(e, 1)}
                  sx={{
                    ...voteBtnBase,
                    ...(currentUserVote === 1
                      ? { background: 'rgba(63,185,80,0.2)', borderColor: '#3FB950', color: '#3FB950' }
                      : { color: 'var(--gs-muted)' })
                  }}
                >
                  <FiChevronUp size={14} />
                </Box>
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, fontWeight: 700, color: '#3FB950', minWidth: 16, textAlign: 'center' }}>
                  {voteScore}
                </AppText>
                <Box
                  component="button"
                  type="button"
                  disabled={voteStore.loading}
                  onClick={(e) => handleVote(e, -1)}
                  sx={{
                    ...voteBtnBase,
                    ...(currentUserVote === -1
                      ? { background: 'rgba(248,81,73,0.2)', borderColor: '#f85149', color: '#f85149' }
                      : { color: 'var(--gs-muted)' })
                  }}
                >
                  <FiChevronDown size={14} />
                </Box>
              </>
                )}
          </Box>
          {buildCount > 0 && (
            <AppText sx={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--gs-accent)' }}>
              {buildCount} builds
            </AppText>
          )}
        </Box>
      </Box>
    </Box>
  )
})
